import { DOCUMENT } from '@angular/common';
import { AfterViewInit, ContentChild, ContentChildren, Directive, ElementRef, Inject, Input, OnDestroy, QueryList } from '@angular/core';
import { fromEvent, takeUntil } from 'rxjs';
import { Subscription } from 'rxjs/internal/Subscription';
import { FreeDraggingHandleDirective } from './free-dragging-handle.directive';

@Directive({
    selector: '[appFreeDragging]',
    standalone: false
})
export class FreeDraggingDirective implements AfterViewInit, OnDestroy{

  private element: HTMLElement;

  private subscriptions: Subscription[] = [];

  @ContentChildren(FreeDraggingHandleDirective, {descendants: true}) handlers: QueryList<FreeDraggingHandleDirective>;
  handleElement: HTMLElement;

  private readonly DEFAULT_DRAGGING_BOUNDARY_QUERY = "body";
  @Input() boundaryQuery = this.DEFAULT_DRAGGING_BOUNDARY_QUERY;
  @Input() xOffSet:number = 0;
  @Input() yOffSet:number  = 0;
  draggingBoundaryElement: HTMLElement | HTMLBodyElement;
  // initialize helper variables
  initialX:number;
  initialY:number;
  currentX:number = 0;
  currentY:number = 0;
  minBoundX:number = 0;
  minBoundY:number = 0;
  maxBoundX:number = 0;
  maxBoundY:number = 0;
  constructor(
    private elementRef: ElementRef,
    @Inject(DOCUMENT) private document: any
  ) {}

  ngAfterViewInit(): void {
    this.draggingBoundaryElement = (this.document as Document).querySelector(
      this.boundaryQuery
    );
    if (!this.draggingBoundaryElement) {
      throw new Error(
        "Couldn't find any element with query: " + this.boundaryQuery
      );
    } else {
      this.element = this.elementRef.nativeElement as HTMLElement;
      this.initDrag();
    }
  }

  initDrag(): void {
    // initialize events
    const dragStart$  = [];
    if(this.handlers.toArray().length > 0){
      this.handlers.toArray().forEach(handle => {
        dragStart$.push(fromEvent<MouseEvent>(handle.elementRef.nativeElement, "mousedown"))
      });
    }else{
      dragStart$.push(fromEvent<MouseEvent>(this.element, "mousedown"));
    }

    const dragEnd$ = fromEvent<MouseEvent>(this.document, "mouseup");
    const drag$ = fromEvent<MouseEvent>(this.document, "mousemove").pipe(
      takeUntil(dragEnd$)
    );

    // initialize helper variables
    this.currentX = 0,
    this.currentY = 0;

    let dragSub: Subscription[] = [];
    this.minBoundX = this.draggingBoundaryElement.offsetLeft - this.element.offsetLeft + this.xOffSet;
    this.minBoundY = this.draggingBoundaryElement.offsetTop - this.element.offsetTop + this.yOffSet;
    this.maxBoundX = this.minBoundX + this.draggingBoundaryElement.offsetWidth - this.element.offsetWidth - this.xOffSet;
    this.maxBoundY = this.minBoundY + this.draggingBoundaryElement.offsetHeight - this.element.offsetHeight - this.yOffSet;
    // Drag starts on click the element (mousedown)
    const allDragSubs = dragStart$.map(subs => {
      return subs.subscribe((event: MouseEvent) => {
        dragSub.push(this.movingElement(event, drag$));
      });
    })

    // Drag end on release the mouse click (mouseup)
    allDragSubs.push(dragEnd$.subscribe(() => {
      this.initialX = this.currentX;
      this.initialY = this.currentY;
      this.element.classList.remove('free-dragging');
      if (dragSub) {
        dragSub.forEach(sub => sub.unsubscribe());
      }
    }));

    allDragSubs.concat(dragSub);
    // Save subscriptions
    this.subscriptions.push.apply(this.subscriptions, allDragSubs);
  }

  movingElement(event: MouseEvent, drag$): Subscription {
    this.initialX = event.clientX - this.currentX;
    this.initialY = event.clientY - this.currentY;
    this.element.classList.add('free-dragging');

    // Listen for drag (mousemove) events
    let dragSub = drag$.subscribe((event: MouseEvent) => {
      event.preventDefault();

      const x = event.clientX - this.initialX;
      const y = event.clientY - this.initialY;

      this.currentX = Math.max(this.minBoundX, Math.min(x, this.maxBoundX));
      this.currentY = Math.max(this.minBoundY, Math.min(y, this.maxBoundY));

      this.element.style.transform =
        "translate3d(" + this.currentX + "px, " + this.currentY + "px, 0)";
    });

    return dragSub;
  }

  ngOnDestroy(): void {
    // Unsubscribe all subscriptions
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

}
